import bs58 from 'bs58'

/**
 * Whether a string is a Solana address.
 *
 * This replaces `PublicKey.isOnCurve(address)` wrapped in a `Result.try`, which
 * is what the network validator used to do. That call discarded its own answer:
 * an address off the curve — every program-derived address is — came back
 * `false` and was still accepted, because only the absence of a throw decided
 * the outcome. What it actually tested was whether `new PublicKey` could parse
 * the string, and that is what is written out here.
 *
 * It is written out because the import cost six hundred kilobytes of
 * `@solana/web3.js` in the chunk every session downloads, to reach a decoder.
 * `solana-address.test.ts` holds the two against each other, case by case and
 * over ten thousand random strings, so the equivalence is checked rather than
 * asserted.
 */

/** An ed25519 public key, which is what a Solana address encodes. */
const ADDRESS_BYTES = 32

export const isSolanaAddress = (address: string): boolean => {
  try {
    return bs58.decode(address).length === ADDRESS_BYTES
  } catch {
    // Not base58 at all — a checksummed Ethereum address, a TON address, a
    // typo. `decode` throws on the characters base58 leaves out: 0, O, I, l.
    return false
  }
}
